import api from './api';

export type CouponColor = 'blue' | 'red' | 'purple' | 'yellow';
export type CouponReceiveMethod = 'manual' | 'order_gift' | 'new_user' | 'issue' | 'member_cycle';
export type CouponType = 'cash' | 'discount';
export type CouponThresholdType = 'none' | 'full';
export type CouponApplyScope = 'all' | 'category' | 'product';
export type CouponEffectiveType = 'immediate' | 'fixed_time';

export interface ServiceCoupon {
  id: string;
  name: string;
  color: CouponColor;
  receiveMethod: CouponReceiveMethod;
  couponType: CouponType;
  discountAmount: number;
  discountRate: number;
  thresholdType: CouponThresholdType;
  minOrderAmount: number;
  applyScope: CouponApplyScope;
  categoryIds: string[];
  productIds: string[];
  effectiveType: CouponEffectiveType;
  validDays: number;
  startAt?: string;
  endAt?: string;
  totalQuantity: number;
  receivedQuantity: number;
  remainingQuantity: number;
  sort: number;
  status: number;
  remark?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceCouponListParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  receiveMethod?: CouponReceiveMethod;
  applyScope?: CouponApplyScope;
  status?: number;
}

export interface ServiceCouponListResponse {
  list: ServiceCoupon[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SaveServiceCouponRequest {
  name: string;
  color: CouponColor;
  receiveMethod: CouponReceiveMethod;
  couponType: CouponType;
  discountAmount?: number;
  discountRate?: number;
  thresholdType: CouponThresholdType;
  minOrderAmount?: number;
  applyScope: CouponApplyScope;
  categoryIds?: string[];
  productIds?: string[];
  effectiveType: CouponEffectiveType;
  validDays?: number;
  startAt?: string;
  endAt?: string;
  totalQuantity?: number;
  receivedQuantity?: number;
  sort?: number;
  status?: number;
  remark?: string;
}

export const couponColorOptions = [
  { label: '蓝色', value: 'blue' as CouponColor, color: 'blue' },
  { label: '红色', value: 'red' as CouponColor, color: 'red' },
  { label: '紫色', value: 'purple' as CouponColor, color: 'purple' },
  { label: '黄色', value: 'yellow' as CouponColor, color: 'gold' },
];

export const couponReceiveMethodOptions = [
  { label: '领取', value: 'manual' as CouponReceiveMethod },
  { label: '下单赠送', value: 'order_gift' as CouponReceiveMethod },
  { label: '新人券', value: 'new_user' as CouponReceiveMethod },
  { label: '发放', value: 'issue' as CouponReceiveMethod },
  { label: '会员定期发放', value: 'member_cycle' as CouponReceiveMethod },
];

export const couponTypeOptions = [
  { label: '代金券', value: 'cash' as CouponType },
  { label: '折扣券', value: 'discount' as CouponType },
];

export const couponThresholdOptions = [
  { label: '无门槛', value: 'none' as CouponThresholdType },
  { label: '有门槛', value: 'full' as CouponThresholdType },
];

export const couponApplyScopeOptions = [
  { label: '全部商品可用', value: 'all' as CouponApplyScope },
  { label: '指定品类可用', value: 'category' as CouponApplyScope },
  { label: '指定商品可用', value: 'product' as CouponApplyScope },
];

export const couponEffectiveTypeOptions = [
  { label: '即时生效', value: 'immediate' as CouponEffectiveType },
  { label: '固定时间', value: 'fixed_time' as CouponEffectiveType },
];

export const couponColorMap = couponColorOptions.reduce<Record<string, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const couponReceiveMethodMap = couponReceiveMethodOptions.reduce<Record<string, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const couponTypeMap = couponTypeOptions.reduce<Record<string, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const couponApplyScopeMap = couponApplyScopeOptions.reduce<Record<string, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const couponEffectiveTypeMap = couponEffectiveTypeOptions.reduce<Record<string, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const getServiceCoupons = (params?: ServiceCouponListParams) =>
  api.get<ServiceCouponListResponse>('/service-coupons', { params });

export const getServiceCoupon = (id: string) => api.get<ServiceCoupon>(`/service-coupons/${id}`);

export const createServiceCoupon = (data: SaveServiceCouponRequest) =>
  api.post<ServiceCoupon>('/service-coupons', data);

export const updateServiceCoupon = (id: string, data: SaveServiceCouponRequest) =>
  api.put<ServiceCoupon>(`/service-coupons/${id}`, data);

export const deleteServiceCoupon = (id: string) => api.delete(`/service-coupons/${id}`);
